import React, { useRef, useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { useReactToPrint } from 'react-to-print';
import { usePDF, Margin } from 'react-to-pdf';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { useExcelExport } from '../../../../hooks/useExcelExport';
import { CalloutContext } from '@folio/stripes/core';
import { useIntl } from 'react-intl';

const printStyles = `
  @page {
    size: A4 auto;
    width: 100%;
  }
  @media print {
    html, body {
      height: auto !important;
      overflow: initial !important;
      color-adjust: exact;
      -webkit-print-color-adjust: exact;
    }
  }
`;

function ExportManager({
  query,
  reportId,
  reportName,
  formats,
  onExportStart,
  onExportComplete,
  onExportError,
  children,
  displayMethod,
  displayConfig,
}) {
  const [isShowPrintWindow, setIsShowPrintWindow] = useState(false);
  const [confirmFormat, setConfirmFormat] = useState(null);
  const componentRef = useRef(null);
  const promiseResolveRef = useRef(null);
  const callout = useContext(CalloutContext);
  const intl    = useIntl();
  // exportAsShown: true by default when displayMethod is pivot or grouped
  const supportsAsShown = displayMethod === 'pivotTable' || displayMethod === 'groupedView';
  const [exportAsShown, setExportAsShown] = useState(true);

  const { prepareExport, triggerDownload, resetExport, exportStage, exportError, exportProgress, isExporting } = useExcelExport();

  const notifySaveFirst = () => callout.sendCallout({
    message: intl.formatMessage({
      id: 'ui-reports.export.saveReportFirst',
      defaultMessage: 'Please save the report first before exporting.',
    }),
    type: 'warning',
  });

  // Handle Excel export — show confirm modal first if supportsAsShown
  const handleExcelExport = () => {
    if (!reportId) { notifySaveFirst(); return; }
    if (onExportStart) onExportStart('excel');
    if (supportsAsShown) {
      setConfirmFormat('excel');
    } else {
      prepareExport(reportId, 'excel', 'table', {});
    }
  };

  // Handle CSV export — show confirm modal first if supportsAsShown
  const handleCsvExport = () => {
    if (!reportId) { notifySaveFirst(); return; }
    if (onExportStart) onExportStart('csv');
    if (supportsAsShown) {
      setConfirmFormat('csv');
    } else {
      prepareExport(reportId, 'csv', 'table', {});
    }
  };

  // Called when user confirms export from modal
  const handleConfirmExport = (format) => {
    const mode   = (supportsAsShown && exportAsShown) ? displayMethod : 'table';
    const config = (supportsAsShown && exportAsShown) ? (displayConfig || {}) : {};
    prepareExport(reportId, format, mode, config);
  };

  const { toPDF, targetRef } = usePDF({
    method: 'save',
    filename: `${reportName || 'report'}.pdf`,
    page: { margin: Margin.MEDIUM },
  });

  // Handle PDF export
  const handlePDFExport = () => {
    if (!componentRef.current) return;

    if (onExportStart) onExportStart('pdf');
    setIsShowPrintWindow(true);

    const input = document.getElementById('printWindow');
    html2canvas(input)
      .then((canvas) => {
        const imgData = canvas.toDataURL('image/png');
        const pdf = new jsPDF(); // eslint-disable-line new-cap
        pdf.addImage(imgData, 'PNG', 0, 0);
        pdf.save(`${reportName || 'report'}.pdf`);
        if (onExportComplete) onExportComplete('pdf');
      })
      .catch((error) => {
        if (onExportError) onExportError('pdf', error);
      });
  };

  // Handle Print
  const handlePrint = useReactToPrint({
    contentRef: componentRef,
    pageStyle: printStyles,
    documentTitle: reportName || 'Report',
    onBeforePrint: () => {
      return new Promise((resolve) => {
        promiseResolveRef.current = resolve;
        setIsShowPrintWindow(true);
        if (onExportStart) onExportStart('print');
      });
    },
    onAfterPrint: () => {
      promiseResolveRef.current = null;
      setIsShowPrintWindow(false);
      if (onExportComplete) onExportComplete('print');
    },
  });

  // Effect to handle print window state
  useEffect(() => {
    if (isShowPrintWindow && promiseResolveRef.current) {
      promiseResolveRef.current();
    }
  }, [isShowPrintWindow]);

  // Effect to handle export completion/errors
  useEffect(() => {
    if (exportError) {
      if (onExportError) onExportError('excel', exportError);
    } else if (exportStage === 'complete') {
      if (onExportComplete) onExportComplete('excel');
    }
  }, [exportStage, exportError, onExportComplete, onExportError]);

  // Export functions to pass to children
  const exportFunctions = {
    excel: formats.includes('excel') ? handleExcelExport : null,
    csv:   formats.includes('csv')   ? handleCsvExport   : null,
    pdf:   formats.includes('pdf')   ? handlePDFExport   : null,
    print: formats.includes('print') ? handlePrint       : null,
    triggerDownload,
    resetExport,
  };

  const exportState = {
    isExporting: isExporting || isShowPrintWindow,
    exportError,
    exportStage: confirmFormat ? 'confirm' : exportStage,
    exportProgress,
    exportType: exportStage !== 'idle' ? 'Excel' : isShowPrintWindow ? 'Print' : null,
    supportsAsShown,
    exportAsShown,
    setExportAsShown,
    onConfirm: () => {
      const fmt = confirmFormat;
      setConfirmFormat(null);
      handleConfirmExport(fmt);
    },
  };

  const printOptions = {
    componentRef,
    targetRef,
    isShowPrintWindow,
    reportName,
  };

  return children({ exportFunctions, exportState, printOptions });
}

ExportManager.propTypes = {
  query: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  reportId: PropTypes.string,
  reportName: PropTypes.string,
  formats: PropTypes.arrayOf(PropTypes.oneOf(['excel', 'pdf', 'print'])),
  onExportStart: PropTypes.func,
  onExportComplete: PropTypes.func,
  onExportError: PropTypes.func,
  children: PropTypes.func.isRequired,
  displayMethod: PropTypes.string,
  displayConfig: PropTypes.object,
};

ExportManager.defaultProps = {
  formats: ['excel', 'pdf', 'print'],
  reportName: 'report',
};

export default ExportManager;
